import { Link } from 'react-router-dom';
import './AgendaVide.css';

/**
 * L'Agenda vide, aucune date nulle part : la page de présentation (ticket 15 de
 * retours-testeurs).
 *
 * « Agenda », une phrase, puis deux grandes cartes côte à côte qui disent à quoi sert chaque
 * vue : la Chronologie, « pour voir venir », et le Calendrier, « pour préparer un jour à
 * l'avance », chacune avec un exemple marqué « Exemple » (comme les pages vides du ticket 14).
 * Dessous, une bande : « Maintenant » s'allège, avec « Poser une date dans un projet » et
 * « Planifier ta journée ».
 *
 * Les exemples sont des images, pas des boutons : rien ne s'y clique.
 */
export function AgendaVide({ onPlanifier }: { onPlanifier: () => void }) {
  return (
    <div className="agenda agenda-vide">
      <header className="agenda-tete">
        <h1>Agenda</h1>
        <p>Tout ce qui est daté, tous dossiers confondus. Deux vues, pour deux questions différentes.</p>
      </header>

      <div className="agenda-vues" data-tuto="agenda-vues">
        <section className="agenda-vue">
          <span className="agenda-vue-nom">Chronologie</span>
          <h2>Qu’est-ce qui arrive&nbsp;?</h2>
          <p>
            Les semaines qui viennent, côte à côte, avec leurs rendez-vous et leurs échéances. Tu vois d’un
            coup d’œil quelle semaine sera chargée.
          </p>
          <div className="agenda-vue-exemple" aria-label="Exemple de Chronologie">
            <span className="agenda-vue-pastille">Exemple</span>
            <div className="agenda-mini-semaines">
              <MiniSemaine titre="28 sept. – 4 oct." cartes={[['rdv', 'Rendez-vous conseiller création', 'Jeu. 1 · 12 h']]} />
              <MiniSemaine
                titre="5 – 11 oct."
                cartes={[
                  ['rdv', 'Conseiller France Travail', 'Lun. 5 · 11 h 30'],
                  ['rdv', 'Relire le prévisionnel', 'Ven. 9 · 16 h'],
                ]}
              />
              <MiniSemaine
                titre="12 – 18 oct."
                cartes={[
                  ['ech', 'Le prévisionnel', 'Mer. 14'],
                  ['rdv', 'Présenter au banquier', 'Ven. 16 · 12 h'],
                ]}
              />
            </div>
          </div>
          <p className="agenda-vue-quand">
            Pour <b>voir venir</b> : ce qui approche, et quand ça se resserre.
          </p>
        </section>

        <section className="agenda-vue">
          <span className="agenda-vue-nom">Calendrier</span>
          <h2>Comment va se passer ce jour&nbsp;?</h2>
          <p>
            Choisis un jour à venir et prépare-le : combien de temps tu auras, quoi faire, dans quel ordre. Tes
            tâches se rangent autour de ce qui est déjà imposé.
          </p>
          <div className="agenda-vue-exemple" aria-label="Exemple de journée préparée">
            <span className="agenda-vue-pastille">Exemple</span>
            <div className="agenda-mini-jour">
              <p>Lundi 5 octobre, préparé</p>
              <MiniCreneau heure="9 h" titre="Rédiger le résumé du projet" />
              <MiniCreneau heure="10 h" titre="Lister trois noms pour la société" />
              <MiniCreneau heure="11 h 30" titre="Rendez-vous France Travail" rdv />
              <MiniCreneau heure="14 h" titre="Appeler la CCI" />
            </div>
          </div>
          <p className="agenda-vue-quand">
            Pour <b>préparer un jour</b> à l’avance, avant qu’il arrive.
          </p>
        </section>
      </div>

      <div className="agenda-allege">
        <span className="agenda-allege-signe" aria-hidden="true">
          ✓
        </span>
        <p>Et «&nbsp;Maintenant&nbsp;» s’allège : ce que tu as prévu pour un autre jour ne te sera proposé que ce jour-là.</p>
        <Link className="agenda-allege-lien" to="/dossiers">
          Poser une date dans un projet
        </Link>
        <button type="button" className="agenda-planifier" onClick={onPlanifier}>
          Planifier ta journée
        </button>
      </div>
    </div>
  );
}

function MiniSemaine({ titre, cartes }: { titre: string; cartes: ['rdv' | 'ech', string, string][] }) {
  return (
    <div className="agenda-mini-semaine">
      <h3>{titre}</h3>
      {cartes.map(([sorte, t, q]) => (
        <div key={t} className={`agenda-mini-carte agenda-mini-carte--${sorte}`}>
          {t}
          <small>{q}</small>
        </div>
      ))}
    </div>
  );
}

function MiniCreneau({ heure, titre, rdv = false }: { heure: string; titre: string; rdv?: boolean }) {
  return (
    <div className="agenda-mini-creneau">
      <span>{heure}</span>
      <span className={rdv ? 'agenda-mini-item agenda-mini-item--rdv' : 'agenda-mini-item'}>{titre}</span>
    </div>
  );
}
